import React, { ReactNode } from 'react';
import { Box, Grid, Theme, useTheme } from '@mui/material';
import { ReactComponent as PersonalisationIcon } from '../../assets/icons/Personalisation.svg';
import { ReactComponent as LocationIcon } from '../../assets/icons/Location.svg';
import { ReactComponent as OrganisationIcon } from '../../assets/icons/Company_Organisation.svg';
import { ReactComponent as ExternalCompaniesIcon } from '../../assets/icons/Company External.svg';
import { ReactComponent as ReportingPeriodIcon } from '../../assets/icons/Date Month.svg';
import { ReactComponent as ReportingPeriodGroupIcon } from '../../assets/icons/Date Year.svg';
import { ReactComponent as StandardIcon } from '../../assets/icons/Standard.svg';
import { ReactComponent as MetricIcon } from '../../assets/icons/Metrics.svg';
import { ReactComponent as OtherConfigurationIcon } from '../../assets/icons/Configuration-50.svg';
import ConfigurationCard from '../../components/shared/cards/ConfigurationCard';
import { UserContext } from '../../context/UserContext';
import { ViewAccess, getViewAccess } from '../../util/user_access';
import LoadingScreen from '../../components/shared/loading/LoadingScreen';
import AccessDenied from '../shared/AccessDenied';
import { GroupContext } from '../../context/GroupContext';
import { CompanyContext } from '../../context/CompanyContext';
import { generateUuidV4 } from '../../util/uuid';
import { log } from '../../util/log';
import { MetadataError } from '@ep/error-handling';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import PrimaryDivider from '../../components/shared/decoration/PrimaryDivider';
import { MinorHeading } from '../../components/shared/typography/PrimaryTypography';
import { useModuleDurationTracking } from '../../hooks/useModuleDurationTracking';

interface ConfigurationGroup {
  title: string;
  configuration_items: Array<ConfigurationItem>;
}

interface ConfigurationItem {
  title: string;
  icon: ReactNode;
  description: string;
  route: string;
}

const ConfigurationDashboard = () => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const { user } = React.useContext(UserContext);
  const { group } = React.useContext(GroupContext);
  const { company } = React.useContext(CompanyContext);

  if (!group || !company) return null;

  // Track module duration.
  useModuleDurationTracking('Configuration', 'N/A', group.id, company.id);

  const [access, setAccess] = React.useState<ViewAccess>(null);
  const theme: Theme = useTheme();
  const primary_blue: string = theme.palette.primary.main;

  const configuration_settings: Array<ConfigurationGroup> = [
    {
      title: 'Represent your Company Entities in Syntiro',
      configuration_items: [
        {
          title: 'Personalisation',
          description: 'Fully brand your Company Portal',
          icon: (
            <PersonalisationIcon height={40} stroke={primary_blue} fill="none" strokeWidth={3} />
          ),
          route: 'configuration/personalisation'
        },
        {
          title: 'Geographical',
          icon: <LocationIcon height={40} fill={primary_blue} stroke={primary_blue} />,
          description: 'Countries and Regions of operation',
          route: 'configuration/geographical'
        },
        {
          title: 'Organisation',
          icon: <OrganisationIcon height={40} fill={primary_blue} stroke={primary_blue} />,
          description: 'Structure your Divisions, Subdivisions and Sites',
          route: 'configuration/organisation'
        },
        {
          title: 'External Companies',
          icon: <ExternalCompaniesIcon height={40} fill={primary_blue} stroke={primary_blue} />,
          description: 'Track partner company Metrics alongside yours',
          route: 'configuration/external-companies'
        }
      ]
    },
    {
      title: 'Track Metric activity over time',
      configuration_items: [
        {
          title: 'Reporting Periods',
          description: 'Distinct periods to Report and Capture by',
          icon: <ReportingPeriodIcon height={40} fill={primary_blue} stroke={primary_blue} />,
          route: 'configuration/reporting-periods'
        },
        {
          title: 'Reporting Period Groups',
          icon: <ReportingPeriodGroupIcon height={40} fill={primary_blue} stroke={primary_blue} />,
          description: 'Organise tracking on higher level time periods',
          route: 'configuration/reporting-period-groups'
        }
      ]
    },
    {
      title: 'Define ESG Data to Capture and Report on',
      configuration_items: [
        {
          title: 'Standards',
          description: 'Choose from global ESG templates',
          icon: <StandardIcon height={40} fill={primary_blue} stroke={primary_blue} />,
          route: 'configuration/standards'
        },
        {
          title: 'Metrics',
          icon: <MetricIcon height={40} fill={primary_blue} stroke={primary_blue} />,
          description: 'Specify your key data indicators',
          route: 'configuration/metrics'
        },
        {
          title: 'Other Configuration',
          icon: <OtherConfigurationIcon height={40} fill={primary_blue} stroke={primary_blue} />,
          description: 'Configure additional data required by your Standards',
          route: 'configuration/other-configuration'
        }
      ]
    }
  ];

  React.useEffect(() => {
    try {
      setAccess(
        group && company && user ? getViewAccess(user, group.id, company.id, 'configuration') : null
      );
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: ConfigurationDashboard failed on an unknown error while initialising.',
          {
            user: user,
            group: group,
            company: company
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Could not verify user access. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  }, [user, company]);

  return !user || access === null ? (
    <LoadingScreen />
  ) : access !== 'none' ? (
    <Box sx={{ mx: 16, my: 4 }}>
      <Grid container spacing={6} sx={{ textAlign: 'left' }}>
        {configuration_settings.map((config_group: ConfigurationGroup) => {
          return (
            <Grid size={12} key={config_group.title}>
              <MinorHeading weight="Bold">{config_group.title}</MinorHeading>
              <PrimaryDivider opacity={0.7} sx={{ my: 2 }} />
              <Grid container spacing={8}>
                {config_group.configuration_items.map((config_item: ConfigurationItem) => {
                  return (
                    <Grid size={4} key={config_item.title}>
                      <ConfigurationCard
                        title={config_item.title}
                        icon={config_item.icon}
                        description={config_item.description}
                        route={config_item.route}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  ) : (
    <AccessDenied />
  );
};

export default ConfigurationDashboard;
